import React, { useState } from 'react';
import { Box, IconButton, Tooltip, Typography } from '@mui/material';
import { ConfirmDialog } from '../../../../../../shared/components/ui/ConfirmDialog';
import { GridColDef } from '@mui/x-data-grid';
import RotateLeftIcon from '@mui/icons-material/RotateLeft';
import BlockIcon from '@mui/icons-material/Block';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { ApiClientSecret } from '../../../../domain/models/ApiClient';
import { ApiClientSecretModal } from './ApiClientSecretModal';
import { useAdminPreferences } from '../../../../application/hooks/useAdminPreferences';
import { formatDate } from '../../../../../../shared/utils/dateUtils';
import { StatusBadge } from '../../../../../../shared/components/ui/StatusBadge';
import { DataTable } from '../../../../../../shared/components/ui/DataTable';

import { colors } from '../../../../../../shared/constants/colors';

interface ApiClientSecretsTableProps {
  secrets: ApiClientSecret[];
  apiClientId: string;
  onRevoke: (secretId: string) => Promise<void>;
  onRotate: (secretId: string) => Promise<{ secret: string }>;
}

function getStatusColor(key: string): string {
  switch (key) {
    case 'active':
      return colors.success;
    case 'revoked':
      return colors.error;
    case 'expired':
      return colors.warning;
    default:
      return colors.disabled;
  }
}

export const ApiClientSecretsTable: React.FC<ApiClientSecretsTableProps> = ({
  secrets,
  apiClientId: _apiClientId,
  onRevoke,
  onRotate,
}) => {
  const [selectedSecret, setSelectedSecret] = useState<ApiClientSecret | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [rotatingSecretId, setRotatingSecretId] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [pendingRevokeSecretId, setPendingRevokeSecretId] = useState<string | null>(null);
  const { language, timezone } = useAdminPreferences();

  const handleViewDetails = (secret: ApiClientSecret) => {
    setSelectedSecret(secret);
    setModalOpen(true);
  };

  const handleRotate = async (secretId: string) => {
    try {
      setRotatingSecretId(secretId);
      const result = await onRotate(secretId);
      setNewSecret(result.secret);
    } finally {
      setRotatingSecretId(null);
    }
  };

  const handleRevokeClick = (secretId: string) => {
    setPendingRevokeSecretId(secretId);
    setRevokeDialogOpen(true);
  };

  const handleRevokeConfirm = async () => {
    if (!pendingRevokeSecretId) return;
    setRevokeDialogOpen(false);
    await onRevoke(pendingRevokeSecretId);
    setPendingRevokeSecretId(null);
  };

  const handleRevokeCancel = () => {
    setRevokeDialogOpen(false);
    setPendingRevokeSecretId(null);
  };

  const columns: GridColDef[] = [
    {
      field: 'secretHint',
      headerName: 'Secret Hint',
      flex: 1,
      minWidth: 85,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
          {params.value || 'N/A'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const status = params.value as { key: string; value: string };
        return <StatusBadge label={status.value} statusColor={getStatusColor(status.key)} />;
      },
    },
    {
      field: 'createdAt',
      headerName: 'Created At',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {formatDate(params.value, { language, timezone, includeTime: true })}
        </Typography>
      ),
    },
    {
      field: 'expiresAt',
      headerName: 'Expires At',
      width: 180,
      renderCell: (params) => (
        <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
          {formatDate(params.value, { language, timezone, includeTime: true })}
        </Typography>
      ),
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => {
        const secret = params.row as ApiClientSecret;
        const isRevoked = secret.status.key === 'revoked';
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
            <Tooltip title="View Details">
              <IconButton size="small" onClick={() => handleViewDetails(secret)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {!isRevoked && (
              <>
                <Tooltip title="Rotate Secret">
                  <IconButton
                    size="small"
                    onClick={() => handleRotate(secret.id)}
                    disabled={rotatingSecretId === secret.id}
                  >
                    <RotateLeftIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Revoke Secret">
                  <IconButton
                    size="small"
                    onClick={() => handleRevokeClick(secret.id)}
                    color="error"
                  >
                    <BlockIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
          </Box>
        );
      },
    },
  ];

  return (
    <>
      <DataTable
        rows={secrets.map((s) => ({ ...s, id: s.id }))}
        columns={columns}
        containerSx={{ height: 400, mt: 0 }}
        emptyMessage="No secrets configured for this API client"
        initialState={{
          pagination: { paginationModel: { pageSize: 10, page: 0 } },
          sorting: { sortModel: [{ field: 'createdAt', sort: 'desc' }] },
        }}
        pageSizeOptions={[10, 25, 50]}
        sx={{ borderLeft: 0, borderRight: 0, borderBottom: 0, borderRadius: 0 }}
      />

      <ApiClientSecretModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setSelectedSecret(null);
          setNewSecret(null);
        }}
        secret={selectedSecret}
        newSecret={newSecret}
      />

      <ConfirmDialog
        open={revokeDialogOpen}
        title="Revoke Secret"
        description="Are you sure you want to revoke this secret? This action cannot be undone."
        confirmLabel="Revoke"
        onConfirm={handleRevokeConfirm}
        onCancel={handleRevokeCancel}
      />
    </>
  );
};
